import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import useStyles from "../../../components/pageTitle/styles";
import TableDesign, {
  TableHeading,
} from "../../../components/common/table/TableDesign";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import {
  addPayConfirmationAction,
  getPayConfirmationListAction,
  setAddPayConfirmationResponse,
} from "../../../store/reducers/paymentSlice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface PayConfirmationListProps {
  // Add props as needed
}

export const tableHeadings: TableHeading[] = [
  { label: "LeadID", align: "left" as const },
  { label: "Customer Name", align: "left" as const },
  { label: "Address", align: "left" as const },
  { label: "Contact Number", align: "left" as const },
  { label: "Relating To", align: "left" as const },
  { label: "Total (£)", align: "left" as const },
  { label: "Amount Due (£)", align: "left" as const },
  { label: "Amount Taken (£)", align: "left" as const },
  { label: "Status", align: "center" as const },
  { label: "Date", align: "center" as const },
  { label: "Invoice Required", align: "center" as const },
  { label: "Auto Send", align: "center" as const },
];
const PayConfirmationList: React.FC<PayConfirmationListProps> = () => {
  const classes = useStyles();
  const { userProfile } = useSelector((state: any) => state?.user);
  const { payConfirmations, isAddPayConfirmationResponse } = useSelector(
    (state: any) => state.payment,
  );
  const dispatch = useDispatch();
  const today = dayjs().format("YYYY-MM-DD");
  const [filterOption, setFilterOption] = React.useState({
    date: today,
  });

  const [selectedRows, setSelectedRows] = useState<
    {
      lead_id: string;
      quote_id: string;
      create_invoice: boolean;
      send_email: boolean;
    }[]
  >([]);

  const handleRangeShift = (
    unit: "week" | "month" | "day",
    direction: "backward" | "forward",
  ) => {
    const multiplier = direction === "backward" ? -1 : 1;

    const newStart = dayjs(filterOption.date).add(multiplier, unit);

    setFilterOption({
      ...filterOption,
      date: newStart.format("YYYY-MM-DD"),
    });
  };

  useEffect(() => {
    // Fetch pay confirmations based on filterOption
    if (filterOption?.date) {
      dispatch(getPayConfirmationListAction(filterOption?.date));
    }
  }, [filterOption]);

  useEffect(() => {
    // Refetch list after successful add operation
    if (isAddPayConfirmationResponse) {
      dispatch(getPayConfirmationListAction(filterOption?.date));
      dispatch(setAddPayConfirmationResponse(false));
      setSelectedRows([]); // Clear selected checkboxes after success
    }
  }, [isAddPayConfirmationResponse]);

  const getJobTypeLabel = (services: { name: string }[] = []) => {
    const names = services?.map((s) => s?.name?.toLowerCase()) || [];

    const hasRemoval = names.includes("removal");
    const hasPacking = names.includes("packing") || names.includes("full pack");

    if (hasRemoval && hasPacking) return "Domestic Pack-Move";
    if (hasRemoval) return "Domestic-Move";

    return "-";
  };

  const handleCheckBoxChange = (
    val: any,
    field: "create_invoice" | "send_email",
    checked: boolean,
  ) => {
    setSelectedRows((prev) => {
      const index = prev.findIndex((row) => row.quote_id === val.quote_id);

      // 🟢 If row already exists → update it
      if (index !== -1) {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          [field]: checked,
        };
        return updated;
      }

      // 🟢 If row does NOT exist → add new
      return [
        ...prev,
        {
          lead_id: val.lead_id,
          quote_id: val.quote_id,
          create_invoice: field === "create_invoice" ? checked : false,
          send_email: field === "send_email" ? checked : false,
        },
      ];
    });
  };

  const getSelectedRow = (quoteId: string) =>
    selectedRows.find((row) => row.quote_id === quoteId);

  console.log("selectedRows", selectedRows);

  const handleSubmit = () => {
    // Submit logic here
    console.log("Submitting selected rows:", selectedRows);
    dispatch(addPayConfirmationAction({ actions: selectedRows }));
  };

  const handleDateChange = (value: string) => {
    setFilterOption({
      ...filterOption,
      date: value,
    });
  };

  return (
    <>
      <div className={classes.pageTitleContainer}>
        {/* <Typography
          sx={{
            textTransform: "capitalize !important",
            display: "flex",
            alignItems: "center",
          }}
          variant={"h5"}
          fontWeight={700}
        >
          Pay Confirmation
        </Typography> */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent={"center"}
          // gap={1}
          mb={1}
        >
          <IconButton
            onClick={() => handleRangeShift("day", "backward")}
            size="small"
            className={classes.iconBtnSmall}
          >
            <ChevronLeft />
          </IconButton>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={filterOption.date ? dayjs(filterOption.date) : null}
              onChange={(newValue) => {
                if (!newValue) return;

                handleDateChange(newValue.format("YYYY-MM-DD"));
              }}
              format={userProfile?.dateFormat || "DD/MM/YYYY"}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    width: 160,
                    "& .MuiInputBase-root": {
                      height: 32,
                      fontSize: 13,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: 13,
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>

          <IconButton
            onClick={() => handleRangeShift("day", "forward")}
            size="small"
            className={classes.iconBtnSmall}
          >
            <ChevronRight />
          </IconButton>

          <Button
            variant="outlined"
            size="small"
            sx={{ ml: 1, textTransform: "none", height: 30 }}
            onClick={() => setFilterOption({ date: today })}
          >
            Reset Filters
          </Button>
        </Box>
        <Button
          variant="contained"
          size="small"
          sx={{ ml: 1, textTransform: "none", height: 30 }}
          onClick={handleSubmit}
        >
          Save & Send
        </Button>
      </div>
      <TableDesign
        tableHeadings={tableHeadings}
        pagination={{ totalPages: 1, currentPage: 1 }}
        filterOption={filterOption}
        stickyHeader={true}
        setFilterOption={setFilterOption}
        tableHeadColor={"#02906f"}
        cellColor={"#fff"}
        extraCSS={{
          height: "calc(100% - 100px)",
        }}
      >
        {payConfirmations.length > 0 ? (
          payConfirmations.map((val: any, idx: number) => (
            <TableRow
              key={idx}
              sx={{
                cursor: "pointer",
                marginTop: "10px",
                padding: "4px !important",
                borderRadius: "20px !important",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  outline: "2px solid #f0f0f0",
                  borderRadius: "0px !important",
                  outlineOffset: "-2px !important",
                },
              }}
            >
              <TableCell align="left">{val?.lead_id}</TableCell>
              <TableCell>{val?.customer_name}</TableCell>
              <TableCell>
                {val?.val?.collection_addresses
                  ? `${val?.collection_addresses[0]?.street} ${val?.collection_addresses[0]?.town} ${val?.collection_addresses[0]?.county} ${val?.collection_addresses[0]?.postcode}`
                  : ""}
              </TableCell>
              <TableCell>{val?.customer_phone}</TableCell>
              <TableCell>{getJobTypeLabel(val?.services)}</TableCell>
              <TableCell>£{val?.total_amount ?? "0.00"}</TableCell>
              <TableCell>
                £
                {(
                  Number(val?.total_amount) - Number(val?.total_payments)
                ).toFixed(2)}
              </TableCell>
              <TableCell>£{val?.total_payments ?? "0.00"}</TableCell>
              <TableCell>{val?.status}</TableCell>
              <TableCell>
                {val?.pay_date ? dayjs(val?.pay_date).format("DD/MM/YYYY") : ""}
              </TableCell>
              <TableCell>
                {val?.invoice_number ? (
                  val?.invoice_number
                ) : (
                  <Checkbox
                    checked={
                      getSelectedRow(val.quote_id)?.create_invoice || false
                    }
                    onChange={(e) =>
                      handleCheckBoxChange(
                        val,
                        "create_invoice",
                        e.target.checked,
                      )
                    }
                  />
                )}
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={getSelectedRow(val.quote_id)?.send_email || false}
                  onChange={(e) =>
                    handleCheckBoxChange(val, "send_email", e.target.checked)
                  }
                />
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={12} align="center">
              No data found.
            </TableCell>
          </TableRow>
        )}
      </TableDesign>
    </>
  );
};

export default PayConfirmationList;
